/** The replay simulator's reads (S1 endpoints).
 *
 * Everything here comes from the ARCHIVE, never a broker: a past minute is
 * immutable, needs no login, and works when the market is shut — which is the
 * entire point of replaying one.
 *
 * Nulls are meaningful throughout. A session can be missing an index minute,
 * and a strike can be missing one of its two sides, so `spot`, the derived
 * header figures and either leg may legitimately be absent. Rendering a zero
 * in their place would read as a price rather than as an absence.
 */

import { fetchOrThrow, getJson } from "./http";

/** One side of one strike at one minute. Greeks are solved per bar at capture
 * time and stay null when the premium is unsolvable. */
export interface SimQuote {
  ltp: number;
  volume: number;
  /** Open interest at this bar. Null for every session archived before
   * 2026-08-28 — it was not captured then and cannot be backfilled. */
  oi: number | null;
  iv: number | null;
  delta: number | null;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
  /** ISO minute this quote printed. Earlier than the response's bar_start
   * when the contract has not traded since - the table dims those. */
  as_of: string | null;
}

export interface SimChainRow {
  strike: number;
  call: SimQuote | null;
  put: SimQuote | null;
}

export interface SimChain {
  underlying: string;
  trade_date: string;
  /** The minute actually served, echoed back in IST so the page never has to
   * trust its own arithmetic about what it asked for. */
  bar_start: string;
  expiry: string;
  /** Every expiry archived for this session — the chain's tabs. */
  expiries: string[];
  spot: number | null;
  atm_strike: number | null;
  /** Put-call parity at the ATM, not a stored price. For every session
   * archived before 2026-08-31 this is the only future available. */
  synthetic_future: number | null;
  straddle_premium: number | null;
  atm_iv: number | null;
  /** Put/call OI ratio across the chain. Null when the session predates OI
   * capture — a 0 would read as "no puts open". */
  pcr: number | null;
  rows: SimChainRow[];
}

/** What one index can be replayed over. The API returns these ordered by
 * session count, most replayable first. */
export interface SimSessions {
  underlying: string;
  sessions: string[];
  first: string;
  last: string;
}

/** One leg of a replayed basket. `entry` is the premium at the minute the
 * leg was ADDED — the server has no memory of when that was, so the client
 * carries it. Re-deriving it would silently reprice every entry each time
 * the clock moved. */
export interface SimLeg {
  strike: number;
  side: "CE" | "PE";
  direction: "BUY" | "SELL";
  lots: number;
  entry: number;
  /** "HH:MM" IST — the minute the leg was added. The MTM curve earns
   * nothing before it, and the payoff refuses to price the leg before it,
   * so stepping the clock back past this makes the leg dormant again.
   *
   * ⚠️ NULL, not absent, on a leg in a RESPONSE, and the API returns the
   * full "HH:MM:SS" — compare on the HH:MM prefix, because "11:00" is
   * less than "11:00:00" as a string and a leg would read as not-yet-taken
   * on the one minute it is certainly live. */
  entry_at?: string | null;
  /** ⭐ SET TOGETHER OR NOT AT ALL — the minute the leg was CLOSED and the
   * premium it closed at. A closed leg is realised: booked at `exit`, no
   * longer marking, and a flat offset on the payoff rather than a bend in
   * it. The API rejects one without the other.
   *
   * ⚠️ NULL, not absent, on an open leg in a RESPONSE — the API sends JSON
   * null. Test with `!= null`, never `!== undefined`, or every open leg
   * reads as closed. */
  exit_at?: string | null;
  exit?: number | null;
  /** ⭐ A PREMIUM THE READER TYPED over the archive's, and the minute it was
   * typed at — both or neither. The server uses it ONLY at that minute, so
   * stepping the clock returns to the archive's own price. */
  mark_at?: string | null;
  mark?: number | null;
}

export interface SimLegPriced extends SimLeg {
  quantity: number;
  /** Premium at the replayed minute; null when it could not be priced. */
  ltp: number | null;
  /** True when `ltp` was CALCULATED because the archive holds no bar for
   * this contract — Black-Scholes at the chain's own vol. It must never be
   * shown as if it were a traded price. */
  modelled?: boolean;
  /** True when `ltp` is a price the reader TYPED for this minute. */
  edited?: boolean;
  /** The archive's solved greeks per UNIT, null where it could not solve or
   * the session predates the greeks capture. */
  iv: number | null;
  delta: number | null;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
}

/** Mirrors the basket card's payoff response, so `PayoffChart` renders
 * both from one implementation.
 *
 * `basis` says whether the numbers are rupees or premium points. Rupees
 * need the contract's lot size, which was only captured from 2026-08-28 —
 * before that the endpoint reports points rather than multiplying by a
 * lot size it guessed. */
export interface SimPayoff {
  spot: number | null;
  spots: number[];
  expiry_pnl: number[];
  today_pnl: number[] | null;
  breakevens: number[];
  max_profit: number;
  max_loss: number;
  max_profit_unbounded: boolean;
  max_loss_unbounded: boolean;
  net_entry_cost: number;
  /** What a broker blocks to carry this basket, in `basis` units. null when a
   * short leg is unhedged: that is SPAN + exposure, which needs a live
   * contract, and a replayed session's have expired. The UI shows a dash. */
  margin: number | null;
  last_mtm: number | null;
  basis: "rupees" | "points";
  lot_size: number | null;
  /** Where that lot size came from. "assumed" means today's configured
   * value stood in for a session captured before lot sizes were stored —
   * the rupees are real arithmetic on an assumed multiplier, and the UI
   * has to say so. */
  lot_size_source: "archived" | "assumed" | "none";
  legs: SimLegPriced[];
  /** Basket greeks over the legs live at this minute, already signed and
   * multiplied by quantity. Null when any live leg's greek is missing — a
   * partial sum would read as a smaller position, not an unknown one. */
  net_delta: number | null;
  net_gamma: number | null;
  net_theta: number | null;
  net_vega: number | null;
}

/** One minute of the realised path. `mtm` is null before any leg was
 * entered, or when a leg it needs has no bar — never a partial mark. */
export interface SimMtmPoint {
  at: string;
  mtm: number | null;
  spot: number | null;
}

/** What the basket actually did across the session — the drawdown a
 * payoff chart structurally cannot show. */
export interface SimMtm {
  points: SimMtmPoint[];
  basis: "rupees" | "points";
  lot_size: number | null;
  lot_size_source: "archived" | "assumed" | "none";
  max_profit: number | null;
  max_loss: number | null;
  max_profit_at: string | null;
  max_loss_at: string | null;
  final: number | null;
}

export interface SimSavedLeg {
  strike: number;
  side: "CE" | "PE";
  direction: "BUY" | "SELL";
  lots: number;
  entry: number;
  entry_at: string | null;
}

export interface SimSaved {
  id: string;
  name: string;
  underlying: string;
  trade_date: string;
  expiry: string;
  legs: SimSavedLeg[];
  created_at: string;
}

export const simulatorApi = {
  /** Which indices exist and over which dates — read once, so the pickers
   * only ever offer a combination the archive can actually serve. */
  indices(): Promise<SimSessions[]> {
    return getJson<{ indices: SimSessions[] }>("/api/simulator/sessions").then((r) => r.indices);
  },

  chain(
    params: {
      underlying: string;
      tradeDate: string;
      at: string;
      expiry?: string;
    },
    signal?: AbortSignal,
  ): Promise<SimChain> {
    const query = new URLSearchParams({
      underlying: params.underlying,
      trade_date: params.tradeDate,
      at: params.at,
    });
    if (params.expiry) query.set("expiry", params.expiry);
    return getJson<SimChain>(`/api/simulator/chain?${query.toString()}`, signal ? { signal } : {});
  },

  payoff(params: {
    underlying: string;
    tradeDate: string;
    at: string;
    expiry: string;
    legs: SimLeg[];
  }): Promise<SimPayoff> {
    return getJson<SimPayoff>("/api/simulator/payoff", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        underlying: params.underlying,
        trade_date: params.tradeDate,
        at: params.at,
        expiry: params.expiry,
        legs: params.legs,
      }),
    });
  },

  /** The whole session in one request — see the endpoint's docstring for
   * why this is not 375 calls to /payoff. */
  mtm(params: {
    underlying: string;
    tradeDate: string;
    expiry: string;
    legs: SimLeg[];
  }): Promise<SimMtm> {
    return getJson<SimMtm>("/api/simulator/mtm", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        underlying: params.underlying,
        trade_date: params.tradeDate,
        expiry: params.expiry,
        legs: params.legs,
      }),
    });
  },

  saves: {
    list(): Promise<SimSaved[]> {
      return getJson<SimSaved[]>("/api/simulator/saves");
    },
    create(params: {
      name: string;
      underlying: string;
      tradeDate: string;
      expiry: string;
      legs: SimLeg[];
    }): Promise<SimSaved> {
      return getJson<SimSaved>("/api/simulator/saves", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: params.name,
          underlying: params.underlying,
          trade_date: params.tradeDate,
          expiry: params.expiry,
          legs: params.legs,
        }),
      });
    },
    async remove(id: string): Promise<void> {
      await fetchOrThrow(`/api/simulator/saves/${id}`, { method: "DELETE" });
    },
  },
};
